import React, { useEffect, useRef, useState } from "react";
import {
    Typography,
    Stack,
    useTheme,
    ButtonBase,
    Slide,
    Box,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import AIIcon from "../../img/AI.svg";
import ChevronLeft from "../../img/Arrow_Left.svg";
import ChevronRight from "../../img/Arrow_Right.svg";
import TextField from "../TextField";
import ColoredIcon from "../ColoredIcon";
import Button from "../Button";
import { DisplayMessage } from "../AIDescriptionField";
import { APP_MAX_WIDTH } from "../../constants/layout";
import AICompletions, { TextGenType } from "../../api/AICompletions";
import { getPreferredLanguage } from "../../utils/languageUtils";
import { FONTS } from "../../constants/styles";
import { useSwipeToDismiss } from "../../hooks/useSwipeToDismiss";

interface Props {
    value?: string;
    open: boolean;
    onCancel: () => void;
    onSave: (val?: string) => void;
    displayHistory: DisplayMessage[];
    setDisplayHistory: React.Dispatch<React.SetStateAction<DisplayMessage[]>>;
    contextType: TextGenType;
    uuid: string;
    brandKey: string;
    serviceId?: string;
}

const TextPromptModal = (props: Props) => {
    const {
        open,
        onCancel,
        onSave,
        displayHistory,
        setDisplayHistory,
        contextType,
        uuid,
        brandKey,
        serviceId,
    } = props;

    const theme = useTheme();
    const { sheetRef, handleProps } = useSwipeToDismiss(onCancel);

    const [message, setMessage] = useState<string | undefined>();
    const [userMessage, setUserMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const scrollDown = () =>
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    const initialUserDisplay = (() => {
        switch (contextType) {
            case "wallpaper_cta":
                return "Create a short, catchy booking CTA for my home screen.";
            case "profile_description":
                return "Write a concise profile description that highlights quality and a personal approach.";
            case "service_description":
                return "Describe this service for clients clearly (what’s included • process • benefits).";
            default:
                return "Generate text.";
        }
    })();

    const handleUserMessageSubmit = async () => {
        const trimmedMessage = userMessage.trim();
        if (!trimmedMessage) return;

        setDisplayHistory((prev) => [
            ...prev,
            { role: "user", content: trimmedMessage },
        ]);
        setLoading(true);

        try {
            const lang = getPreferredLanguage();
            let res:
                | Awaited<ReturnType<typeof AICompletions.requestWallpaperCTA>>
                | Awaited<
                      ReturnType<typeof AICompletions.requestProfileDescription>
                  >
                | Awaited<
                      ReturnType<typeof AICompletions.requestServiceDescription>
                  >;

            switch (contextType) {
                case "wallpaper_cta":
                    res = await AICompletions.requestWallpaperCTA(
                        uuid,
                        brandKey,
                        lang,
                        trimmedMessage
                    );
                    break;
                case "profile_description":
                    res = await AICompletions.requestProfileDescription(
                        uuid,
                        brandKey,
                        lang,
                        trimmedMessage
                    );
                    break;
                case "service_description":
                    if (!serviceId) {
                        console.error(
                            "No serviceId provided for service_description"
                        );
                        setLoading(false);
                        return;
                    }
                    res = await AICompletions.requestServiceDescription(
                        uuid,
                        brandKey,
                        serviceId,
                        lang,
                        trimmedMessage
                    );
                    break;
                default:
                    throw new Error(`Unsupported contextType: ${contextType}`);
            }

            const assistantMsg: DisplayMessage = {
                role: "assistant",
                content: res.text,
            };
            setDisplayHistory((prev) => [...prev, assistantMsg]);
            setMessage(res.text);
        } catch (err) {
            console.error("Generation error: ", err);
        } finally {
            setUserMessage("");
            scrollDown();
            setLoading(false);
        }
    };

    // initial generation
    useEffect(() => {
        const run = async () => {
            if (displayHistory.length > 1) {
                const lastAssistant = [...displayHistory]
                    .reverse()
                    .find((m) => m.role === "assistant");
                if (lastAssistant) setMessage(lastAssistant.content);
                return;
            }

            setDisplayHistory([{ role: "user", content: initialUserDisplay }]);
            setLoading(true);

            try {
                const lang = getPreferredLanguage();
                let res:
                    | Awaited<
                          ReturnType<typeof AICompletions.requestWallpaperCTA>
                      >
                    | Awaited<
                          ReturnType<
                              typeof AICompletions.requestProfileDescription
                          >
                      >
                    | Awaited<
                          ReturnType<
                              typeof AICompletions.requestServiceDescription
                          >
                      >;

                switch (contextType) {
                    case "wallpaper_cta":
                        res = await AICompletions.requestWallpaperCTA(
                            uuid,
                            brandKey,
                            lang
                        );
                        break;
                    case "profile_description":
                        res = await AICompletions.requestProfileDescription(
                            uuid,
                            brandKey,
                            lang
                        );
                        break;
                    case "service_description":
                        if (!serviceId) {
                            console.error(
                                "No serviceId provided for initial service_description"
                            );
                            setLoading(false);
                            return;
                        }
                        res = await AICompletions.requestServiceDescription(
                            uuid,
                            brandKey,
                            serviceId,
                            lang
                        );
                        break;
                    default:
                        throw new Error(
                            `Unsupported contextType: ${contextType}`
                        );
                }

                const assistantMsg: DisplayMessage = {
                    role: "assistant",
                    content: res.text,
                };
                setDisplayHistory((prev) => [...prev, assistantMsg]);
                setMessage(res.text);
            } catch (err) {
                console.error("Initial generation error:", err);
            } finally {
                scrollDown();
                setLoading(false);
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
            <Box
                ref={sheetRef}
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                height="95%"
                display="flex"
                justifyContent="center"
            >
                <Stack
                    className="force-dynamic-translation"
                    width="100%"
                    maxWidth={APP_MAX_WIDTH}
                    bgcolor="white"
                    borderRadius="14px 14px 0 0"
                    justifyContent="center"
                    alignItems="center"
                >
                    {/* Drag handle */}
                    <Box
                        sx={{
                            width: 36,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.15)",
                            mt: 1,
                        }}
                    />
                    <Stack
                        {...handleProps}
                        paddingY={1}
                        spacing={2}
                        width="90%"
                        direction="row"
                        alignItems="center"
                        sx={{ touchAction: "none", cursor: "grab" }}
                    >
                        <ButtonBase
                            disabled={loading}
                            onClick={onCancel}
                            sx={{
                                width: "38px",
                                minWidth: "38px",
                                height: "38px",
                            }}
                        >
                            <ColoredIcon
                                icon={ChevronLeft}
                                size="100%"
                                color="black"
                            />
                        </ButtonBase>
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            width="50px"
                            height="50px"
                            borderRadius="50px"
                            bgcolor={theme.palette.primary.main}
                        >
                            <ColoredIcon
                                icon={AIIcon}
                                color="white"
                                size="34px"
                            />
                        </Stack>
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={700}
                            fontSize="20px"
                            lineHeight="30px"
                        >
                            AI Copywriter
                        </Typography>
                    </Stack>
                    <Stack
                        direction="column"
                        height="100%"
                        width="100%"
                        overflow="auto"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                        paddingTop={2}
                        bgcolor={alpha(theme.palette.primary.main, 0.05)}
                    >
                        {displayHistory.map((x, index) => (
                            <MessageBox
                                key={index}
                                message={x}
                                className={
                                    index === 0
                                        ? "force-dynamic-translation"
                                        : ""
                                }
                            />
                        ))}
                        <div ref={messagesEndRef} style={{ marginTop: 10 }} />
                    </Stack>
                    <Stack width="90%" paddingY={2} spacing={2}>
                        <Button
                            disabled={loading}
                            onClick={() => onSave(message)}
                        >
                            Save
                        </Button>
                        <TextField
                            multiline
                            maxRows={5}
                            padding={2}
                            fullWidth
                            variant="outlined"
                            placeholder="Message"
                            value={userMessage}
                            onChange={(e) =>
                                setUserMessage(e.currentTarget.value)
                            }
                            icon={ChevronRight}
                            onClick={() => handleUserMessageSubmit()}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleUserMessageSubmit()
                            }
                        />
                    </Stack>
                </Stack>
            </Box>
        </Slide>
    );
};

const MessageCloud = (props: {
    side: "left" | "right";
    content: string;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const { side, content } = props;

    const borderRadius =
        side === "left" ? "4px 16px 16px 16px" : "16px 16px 4px 16px";
    const bgcolor =
        side === "left" ? alpha(primary, 0.08) : primary;
    const color = side === "left" ? "text.primary" : "white";

    return (
        <Stack
            sx={{
                px: 1.75,
                py: 1,
                bgcolor,
                borderRadius,
                maxWidth: "88%",
                boxShadow:
                    side === "right"
                        ? `0 1px 4px ${alpha(darken(primary, 0.3), 0.2)}`
                        : "none",
            }}
        >
            <Typography
                fontWeight={400}
                fontSize="14px"
                lineHeight="20px"
                color={color}
                sx={{ wordBreak: "break-word" }}
            >
                {content}
            </Typography>
        </Stack>
    );
};

const MessageBox = (props: { message: DisplayMessage; className?: string }) => {
    const { message, className } = props;

    const align = message.role === "assistant" ? "flex-start" : "flex-end";
    const side = message.role === "assistant" ? "left" : "right";
    const content = message.content?.toString() ?? "";

    return (
        <Stack
            width="90%"
            direction="row"
            justifyContent={align}
            className={className}
        >
            <Stack
                width="100%"
                direction="column"
                alignItems={align}
                spacing={0.5}
            >
                <MessageCloud side={side} content={content} />
            </Stack>
        </Stack>
    );
};

export default TextPromptModal;
